import {
  AI_BATCH_FUNCTIONALITIES,
  AI_BATCH_ITEM_STATUSES,
  AI_BATCH_STATUSES,
  AiBatch,
  AiBatchError,
  AiBatchFunctionality,
  AiBatchItem,
  AiBatchItemStatus,
  AiBatchQuota,
  AiBatchSkippedProduct,
  AiBatchStatus,
} from "../../../interfaces/AiBatchObject";
import { coerceSingle } from "../../helpers/ApiEnvelopeHelper";
import { publicUrlFromObjectPath } from "./mediaConvertor";

// The imagegen API is camelCase, so these converters only null-guard, coerce
// numbers and validate the enum strings — a partial or slightly-off response can
// never crash the UI or render NaN. Absent means null, not "".

type Raw = Record<string, unknown>;

const asRecord = (value: unknown): Raw =>
  value && typeof value === "object" ? (value as Raw) : {};

const toRequiredString = (value: unknown): string =>
  value === undefined || value === null ? "" : String(value);

const toNullableString = (value: unknown): string | null => {
  if (value === undefined || value === null) return null;
  const text = String(value);
  return text === "" ? null : text;
};

const toCount = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
};

function oneOf<T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback: T
): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

/**
 * Unknown batch status → INGESTING: still "in progress", but a state that offers
 * neither Cancel nor Revert. A value the client doesn't know must never unlock an
 * action the server may reject — or worse, accept.
 */
const toBatchStatus = (value: unknown): AiBatchStatus =>
  oneOf(value, AI_BATCH_STATUSES, "INGESTING");

/** Unknown item status → QUEUED, the one state that implies nothing happened. */
const toItemStatus = (value: unknown): AiBatchItemStatus =>
  oneOf(value, AI_BATCH_ITEM_STATUSES, "QUEUED");

const toFunctionality = (value: unknown): AiBatchFunctionality =>
  oneOf(value, AI_BATCH_FUNCTIONALITIES, "ENHANCE");

const toImageUrl = (objectPath: unknown): string | null => {
  const path = toNullableString(objectPath);
  return path ? publicUrlFromObjectPath(path) : null;
};

/** `ErrorInformation` → AiBatchError. The backend names the text `errorDetail`. */
function convertError(raw: unknown): AiBatchError | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Raw;
  const code = toNullableString(r.errorCode);
  if (!code) return null;
  return {
    code,
    message: toNullableString(r.errorDetail ?? r.errorMessage),
  };
}

function convertItem(raw: unknown): AiBatchItem {
  const r = asRecord(raw);
  return {
    itemId: toRequiredString(r.itemId),
    productId: toRequiredString(r.productId),
    productTitle: toNullableString(r.productTitle),
    status: toItemStatus(r.status),
    sourceUrl: toImageUrl(r.sourceObjectPath),
    resultUrl: toImageUrl(r.resultObjectPath),
    error: convertError(r.error),
    completedAt: toNullableString(r.completedAt),
  };
}

function convertSkipped(raw: unknown): AiBatchSkippedProduct {
  const r = asRecord(raw);
  return {
    productId: toRequiredString(r.productId),
    reason: toRequiredString(r.reason),
    message: toNullableString(r.message),
  };
}

/** null stays null: the list view omits items/skipped, and the UI tells "not
 *  loaded" apart from "loaded, empty". */
function convertArray<T>(value: unknown, convert: (raw: unknown) => T): T[] | null {
  return Array.isArray(value) ? value.map(convert) : null;
}

/** One AiImageBatchDTO. Accepts an array-wrapped single (`contents: [ {...} ]`). */
export function convertAiBatch(raw: unknown): AiBatch {
  const r = coerceSingle(raw);
  return {
    batchId: toRequiredString(r.batchId),
    businessId: toRequiredString(r.businessId),
    businessName: toNullableString(r.businessName),
    collectionId: toRequiredString(r.collectionId),
    collectionName: toNullableString(r.collectionName),
    submittedBy: toRequiredString(r.submittedBy),
    functionality: toFunctionality(r.functionality),
    instructions: toNullableString(r.instructions),
    status: toBatchStatus(r.status),
    totalItems: toCount(r.totalItems),
    succeededItems: toCount(r.succeededItems),
    failedItems: toCount(r.failedItems),
    countsTowardLimit: r.countsTowardLimit !== false,
    error: convertError(r.error),
    createdAt: toNullableString(r.createdAt),
    submittedAt: toNullableString(r.submittedAt),
    completedAt: toNullableString(r.completedAt),
    revertedAt: toNullableString(r.revertedAt),
    items: convertArray(r.items, convertItem),
    skipped: convertArray(r.skipped, convertSkipped),
  };
}

/** The list endpoint's `contents` — an array of AiImageBatchDTO. Rows without an
 *  id are dropped: they can't be keyed, expanded or acted on. */
export function convertAiBatchList(contents: unknown): AiBatch[] {
  if (!Array.isArray(contents)) return [];
  return contents
    .map((row) => convertAiBatch(row))
    .filter((batch) => batch.batchId !== "");
}

export function convertAiBatchQuota(raw: unknown): AiBatchQuota {
  const r = coerceSingle(raw);
  return {
    businessId: toRequiredString(r.businessId),
    enabled: r.enabled === true,
    exempt: r.exempt === true,
    limit: toCount(r.limit),
    used: toCount(r.used),
    remaining: toCount(r.remaining),
    maxItemsPerBatch: toCount(r.maxItemsPerBatch),
  };
}

export default { convertAiBatch, convertAiBatchList, convertAiBatchQuota };
